import { useState } from "react";

export interface ActionApprovalDisplay {
  kind: string;
  summary: string;
  reason: string;
  requestDigest: string;
  details: Record<string, unknown>;
  requestedCharge: Record<string, unknown>;
}

export function ActionApprovalDetails({
  approval,
  collapsed = false,
}: {
  approval: ActionApprovalDisplay;
  collapsed?: boolean;
}) {
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const details = approval.details;
  const functionName = textValue(details.function);
  const server = textValue(details.server);
  const explanation = textValue(details.action_explanation);
  const parameters = details.arguments;
  const rows = parameterRows(parameters);
  const reason =
    approval.reason === "该动作具有外部副作用或破坏性，必须由你明确确认。"
      ? "这一步被平台标记为需要确认，请核对本次使用的工具和输入后再允许。"
      : approval.reason;
  const scope = (
    <>
      <p>本次申请：使用下列输入调用一次指定工具，授权绑定当前会话和当前任务目标。</p>
      <dl>
        <div>
          <dt>执行工具</dt>
          <dd>{functionName || approval.summary}</dd>
        </div>
        <div>
          <dt>执行服务</dt>
          <dd>{server || "未提供"}</dd>
        </div>
      </dl>
      <div className="approval-inputs">
        <strong>本次输入</strong>
        {rows.length > 0 ? (
          <dl>
            {rows.map((row, index) => (
              <div key={index}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>{parameters === undefined ? "未提供输入详情。" : "未传入参数。"}</p>
        )}
      </div>
    </>
  );
  // 单个审批绑定一个具体函数调用；底层预算和输入条数都不是业务调用次数。
  const toolUsage = (
    <>
      <dl className="approval-resource-grid">
        <div>
          <dt>{functionName || approval.summary}</dt>
          <dd>1 次调用</dd>
        </div>
      </dl>
      <p className="approval-note">本次授权包含以上操作，后续操作另计。</p>
    </>
  );
  return (
    <>
      <div className="action-approval-explanation">
        <strong>这一步做什么</strong>
        <span>
          {explanation ||
            `将本次输入交给 ${functionName || "指定工具"} 执行。工具未提供进一步的用途说明。`}
        </span>
      </div>
      {collapsed ? (
        <>
          <details>
            <summary>为什么需要确认</summary>
            <p>{reason}</p>
          </details>
          <details>
            <summary>允许了什么</summary>
            {scope}
          </details>
          <details>
            <summary>本次工具使用</summary>
            {toolUsage}
          </details>
        </>
      ) : (
        <>
          <section className="approval-section" aria-label="允许了什么">
            <h3>允许了什么</h3>
            {scope}
          </section>
          <section className="approval-section" aria-label="本次工具使用">
            <h3>本次工具使用</h3>
            {toolUsage}
          </section>
          <p className="action-approval-reason">
            <strong>为什么需要确认</strong>
            <span>{reason}</span>
          </p>
        </>
      )}
      <details onToggle={(event) => setTechnicalOpen(event.currentTarget.open)}>
        <summary>技术详情（完整参数与请求指纹）</summary>
        {technicalOpen && (
          <>
            <dl>
              <div>
                <dt>动作类别</dt>
                <dd>{approval.kind}</dd>
              </div>
              <div>
                <dt>请求指纹</dt>
                <dd>
                  <code>{approval.requestDigest}</code>
                </dd>
              </div>
            </dl>
            <pre>{JSON.stringify(approval.details, null, 2)}</pre>
            <h3>底层执行预算</h3>
            <p>这些数值是执行保护上限，不代表本次计算的实际用量或费用。</p>
            <dl className="approval-resource-grid">
              {resourceRows(approval.requestedCharge).map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <pre>{JSON.stringify(approval.requestedCharge, null, 2)}</pre>
          </>
        )}
      </details>
    </>
  );
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

// 仅翻译通用输入字段；未知字段保留原名，不推断工具能力或副作用。
const parameterLabels: Record<string, string> = {
  name: "名称",
  input_json: "输入内容",
  sequences: "序列",
  sequence: "序列内容",
  protein: "蛋白质",
  rna: "RNA",
  dna: "DNA",
  id: "标识",
  job_id: "任务编号",
  path: "路径",
  input_path: "输入路径",
  output_path: "输出路径",
  output_dir: "输出目录",
  file: "文件",
  files: "文件",
  url: "地址",
  timeout: "超时设置",
};

function parameterRows(value: unknown): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [];
  let omitted = false;
  function visit(item: unknown, path: string[], depth: number) {
    if (rows.length >= 12) {
      omitted = true;
      return;
    }
    const label = path.join(" · ") || "输入";
    if (item !== null && typeof item === "object") {
      const entries = Object.entries(item);
      if (entries.length === 0) {
        rows.push({ label, value: Array.isArray(item) ? "空列表" : "空对象" });
      } else if (depth >= 6) {
        rows.push({ label, value: `${entries.length} 项内容，详见技术详情` });
      } else {
        for (const [key, child] of entries) {
          if (rows.length >= 12) {
            omitted = true;
            break;
          }
          visit(
            child,
            [
              ...path,
              Array.isArray(item)
                ? `第 ${Number(key) + 1} 项`
                : Object.hasOwn(parameterLabels, key)
                  ? parameterLabels[key]!
                  : key,
            ],
            depth + 1,
          );
        }
      }
      return;
    }
    const content =
      item === null ? "未设置" : typeof item === "boolean" ? (item ? "是" : "否") : String(item);
    rows.push({
      label,
      value:
        content.length > 80
          ? `${content.slice(0, 80)}…（共 ${content.length} 字符，完整内容见技术详情）`
          : content || "空文本",
    });
  }
  if (value !== undefined) visit(value, [], 0);
  if (omitted)
    rows.push({ label: "更多输入", value: "仅展示前 12 项，请展开技术详情核对完整参数。" });
  return rows;
}

function resourceRows(budget: Record<string, unknown>): Array<[string, string]> {
  const format = (key: string, formatter: (value: number) => string) => {
    const value = budget[key];
    return typeof value === "number" && Number.isFinite(value) && value >= 0
      ? formatter(value)
      : "未提供";
  };
  return [
    [
      "CPU 时间",
      format("cpuMillis", (value) =>
        value < 1000 ? `${value} 毫秒` : `${number(value / 1000)} 秒`,
      ),
    ],
    ["内存", format("memoryBytes", bytes)],
    ["输入数据", format("inputBytes", bytes)],
    ["输出数据", format("outputBytes", bytes)],
    ["外部调用", format("externalCalls", (value) => `${value} 次`)],
  ];
}

function number(value: number): string {
  return value.toLocaleString("zh-CN", { maximumFractionDigits: 2 });
}

function bytes(value: number): string {
  if (value === 0) return "0 B";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${number(value / 1024 ** index)} ${units[index]}`;
}
